<template>
    <!-- Einzelnes Feld ///////////////////////////////////////////////////// -->
    <editor-field-frame v-if="!isRepeatable"
                        :label="props.label"
                        :hint="props.hint"
                        :error="lengthError"
                        :control-id="controlId">
        <template #default="{controlId: id, hintId}">
            <!--
                Zugeklappt mit Vorschau, aufgeklappt der bisherige Editor.
                Ein Fehler (zu lang) hält das Feld offen.
            -->
            <collapsible-text :text="value"
                              :label="props.label"
                              :control-id="id"
                              :described-by="hintId"
                              :force-open="!!lengthError">
                <!--
                    Kein Monospace mehr: Redaktionelle Texte werden in der
                    normalen Schrift bearbeitet. Monospace bleibt den Codefeldern
                    des Expertenmodus vorbehalten (.editor-code-input).
                -->
                <v-textarea v-model="value"
                            v-bind="controlProps"
                            :id="id"
                            :aria-describedby="hintId"
                            auto-grow
                            rows="4" />
            </collapsible-text>
        </template>
    </editor-field-frame>

    <!-- Wiederholbares Feld //////////////////////////////////////////////// -->
    <editor-field-frame v-else :label="props.label" :hint="props.hint">
        <v-card class="editor-section-card" variant="outlined">
            <v-card-text>
                <!--
                    Jeder Eintrag klappt einzeln auf. Die Einträge sind nach
                    Position verschlüsselt, der Auf-Zustand hängt also an der
                    Position: Beim Ziehen und Entfernen klappt alles zu, sonst
                    stünde danach ein anderer Eintrag offen.
                -->
                <draggable :list="value"
                           :item-key="((item) => value.indexOf(item))"
                           handle=".drag-handle"
                           @start="collapseEntries">
                    <template #item="{element, index}">
                        <div class="d-flex align-start mb-3" style="gap: 8px;">
                            <v-icon class="drag-handle mt-3" :title="$t('common.actions.move')">mdi-drag-vertical</v-icon>
                            <collapsible-text :text="element"
                                              :label="props.label"
                                              :collapse-signal="collapseSignal">
                                <v-textarea v-model="value[index]" v-bind="controlProps" auto-grow rows="3" />
                            </collapsible-text>
                            <v-btn variant="text" size="small" density="comfortable" class="mt-2"
                                   prepend-icon="mdi-close"
                                   @click="removeFromList(element)">{{ $t('common.actions.remove') }}</v-btn>
                        </div>
                    </template>
                </draggable>
            </v-card-text>
            <v-card-actions>
                <v-btn variant="text" color="primary" prepend-icon="mdi-plus"
                       class="normal-case" @click="addToList">{{ $t('editor.fields.list.add_entry') }}</v-btn>
            </v-card-actions>
        </v-card>
    </editor-field-frame>
</template>

<script setup>
import {computed, ref} from "vue";
import draggable from "vuedraggable";
import EditorFieldFrame from "./EditorFieldFrame.vue";
import CollapsibleText from "./CollapsibleText.vue";
import {
    mandatory_props,
    useEditorField,
    editorControlProps,
    editorControlId,
} from "../../utils/editorConfigFieldHelpers";
import {useI18n} from "@/i18n";

const {t, formatNumber} = useI18n();

const MAX_LENGTH = 100000;

const props = defineProps(mandatory_props);
const {value, jsonPath} = useEditorField(props);

const isRepeatable = computed(() => Array.isArray(value.value));
const controlProps = computed(() => editorControlProps(props.vuetify_config));
const controlId = computed(() => editorControlId('textarea', jsonPath.value));

/**
 * Fehler erscheint jetzt an derselben Stelle wie bei allen anderen Feldern -
 * unterhalb des Controls in der Feldhülle.
 */
const lengthError = computed(() => {
    if (typeof value.value !== 'string') return null;
    if (value.value.length <= MAX_LENGTH) return null;

    return t('editor.fields.textarea.too_long', {
        length: value.value.length,
        max: formatNumber(MAX_LENGTH),
    });
});

/** Jede Erhöhung klappt alle Listeneinträge zu, siehe CollapsibleText. */
const collapseSignal = ref(0);

function collapseEntries() {
    collapseSignal.value += 1;
}

function addToList() {
    if ( ! Array.isArray(value.value)) return;
    value.value.push("");
}

function removeFromList(text) {
    const index = value.value.indexOf(text);
    if (index > -1) {
        collapseEntries();
        value.value.splice(index, 1);
    }
}
</script>
